import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { homeRouteFor } from "@/components/ProtectedRoute";
import { formatApiErrorDetail } from "@/lib/api";
import logo from "@/assets/planet-smoothie-logo.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export function AuthLayout({ title = "Welcome", description, children }) {
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6 animate-fade-in">
        <div className="flex flex-col items-center space-y-2">
          <img src={logo} alt="Planet Smoothie" className="h-16 w-auto" data-testid="brand-logo" />
          <h1 className="text-2xl font-bold text-foreground">Local Store Marketing</h1>
        </div>
        <Card className="card-shadow">
          <CardHeader className="pb-4">
            <CardTitle>{title}</CardTitle>
            {description && <CardDescription>{description}</CardDescription>}
          </CardHeader>
          <CardContent>{children}</CardContent>
        </Card>
      </div>
    </div>
  );
}

export const Field = ({ label, id, ...props }) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} {...props} />
  </div>
);

export const SubmitBtn = ({ loading, children, ...props }) => (
  <Button type="submit" className="w-full" disabled={loading} {...props}>
    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
    {children}
  </Button>
);

export const ErrorMsg = ({ children }) =>
  children ? (
    <div data-testid="auth-error" className="bg-destructive/10 border border-destructive/30 text-destructive text-sm px-3 py-2 rounded-md">
      {children}
    </div>
  ) : null;

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(homeRouteFor(u));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Welcome" description="Sign in to your marketing dashboard">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <ErrorMsg>{error}</ErrorMsg>
        <Field label="Email" id="email" type="email" required value={email} data-testid="login-email"
          onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
        <Field label="Password" id="password" type="password" required value={password} data-testid="login-password"
          onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
        <SubmitBtn loading={loading} data-testid="login-submit">Login</SubmitBtn>
        <div className="text-center">
          <Link to="/forgot-password" data-testid="forgot-link" className="text-sm text-muted-foreground hover:text-primary">
            Forgot Password?
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}
